import React, { useState, useEffect, useCallback } from 'react';
import { StarFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';

const TestimonialCard = ({ rating, quote, author, company }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg h-full">
    <div className="flex mb-4">
      {[...Array(rating)].map((_, index) => (
        <StarFilled key={index} className="text-yellow-400" />
      ))}
    </div>
    <p className="text-gray-700 mb-4 italic">"{quote}"</p>
    <div className="border-t pt-4">
      <h4 className="font-bold">{author}</h4>
      <p className="text-gray-600">{company}</p>
    </div>
  </div>
);

const testimonials = [
  {
    rating: 5,
    quote: "Our members are so impressed. It's intuitive. It's clean. It's distraction free. If you're building a community.",
    author: "Sabo Masties",
    company: "Founder @ Rolex"
  },
  {
    rating: 5,
    quote: "Our members are so impressed. It's intuitive. It's clean. It's distraction free. If you're building a community.",
    author: "Musharof Chowdhury",
    company: "Founder @ Ayro UI"
  },
  {
    rating: 5,
    quote: "Our members are so impressed. It's intuitive. It's clean. It's distraction free. If you're building a community.",
    author: "William Smith",
    company: "Founder @ Trorex"
  },
  {
    rating: 5,
    quote: "The best solution we've found for managing our retail operations. Highly recommended!",
    author: "Sarah Johnson",
    company: "Manager @ TechStore"
  },
  {
    rating: 5,
    quote: "Incredible features and excellent customer support. Game changer for our business.",
    author: "Mike Chen",
    company: "CEO @ DigitalMart"
  }
];

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [nextSlide, currentSlide]);

  return (
    <div className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4">What Our Clients Say</h2>
        <p className="text-xl text-gray-600 text-center mb-12">
          Our clients have shared how our solutions helped them streamline their operations and improve customer satisfaction.
        </p>
        
        {/* Carousel Container with overlaid buttons */}
        <div className="relative max-w-2xl mx-auto group">
          {/* Carousel Track */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <TestimonialCard {...testimonial} />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons - overlaid with hover effect */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10 opacity-0 group-hover:opacity-100 backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <LeftOutlined className="text-lg" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10 opacity-0 group-hover:opacity-100 backdrop-blur-sm"
            aria-label="Next slide"
          >
            <RightOutlined className="text-lg" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-blue-600 scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile Grid View (hidden on desktop) */}
        <div className="grid md:hidden grid-cols-1 gap-8 mt-8">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonials; 